import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Table, Modal, message, Button, Tabs, Select, DatePicker, Checkbox, Card, Spin } from "antd";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "antd/dist/reset.css";
import { api_forfreight_url } from "../data/urlData";
import SessionManager from "../auth/sessionmanager";
import AppLayout from "../layout/AppLayout";
import { useNavigate } from "react-router-dom";
import "./Request_history.css";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Requesthistory = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  
  // Add loading state for main data
  const [loadingData, setLoadingData] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  // Time series chart states
  const [selectedServices, setSelectedServices] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState({});
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const prevServices = useRef([]);

  const token = SessionManager.getToken();

  const myHeaders = useMemo(() => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);
    headers.append("Content-Type", "application/json");
    return headers;
  }, [token]);

  // Updated fetchData with loading state
  const fetchData = useCallback(() => {
    setLoadingData(true); // Start loading
    
    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(`${api_forfreight_url}/api/requests`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        const sortedData = result.sort((a, b) => new Date(a.request_datetime) - new Date(b.request_datetime));
        setData(sortedData);
      })
      .catch((error) => {
        console.error(error);
        message.error("Failed to fetch request data");
      })
      .finally(() => {
        setLoadingData(false); // Stop loading
      });
  }, [myHeaders]);

  // Fetch available metrics for selected services
  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(`${api_forfreight_url}/api/plots/metrics`, requestOptions);
      const result = await response.json();      

      // Extract unique services from the API response
      const uniqueServicesMap = new Map();

      result.forEach((serviceData) => {
        const key = serviceData.service;
        if (!uniqueServicesMap.has(key)) {
          uniqueServicesMap.set(key, {
            service: serviceData.service,
            description: serviceData.description,
          });
        }
      });

      const servicesFromAPI = Array.from(uniqueServicesMap.values());
      setAvailableServices(servicesFromAPI);

      // Remove duplicates and filter metrics for selected services
      const uniqueServiceData = new Map();
      result.forEach((serviceData) => {
        if (!uniqueServiceData.has(serviceData.service)) {
          uniqueServiceData.set(serviceData.service, serviceData);
        } else {
          // Merge metrics if there are duplicates
          const existing = uniqueServiceData.get(serviceData.service);
          const existingMetricNames = existing.metrics.map((m) => m.name);
          const newMetrics = serviceData.metrics.filter((m) => !existingMetricNames.includes(m.name));
          existing.metrics.push(...newMetrics);
        }
      });

      // Store all available metrics for all services
      const allServiceMetrics = {};
      uniqueServiceData.forEach((serviceData, serviceName) => {
        allServiceMetrics[serviceName] = serviceData.metrics;
      });
      setAvailableMetrics(allServiceMetrics);

      // Filter selected services to only include those available from API
      setSelectedServices((prev) => prev.filter((service) => servicesFromAPI.includes(service)));

      // Filter selected metrics to only include those available for the current services
      const availableMetricNames = new Set();
      if (selectedServices.length > 0) {
        selectedServices.forEach((service) => {
          if (allServiceMetrics[service]) {
            allServiceMetrics[service].forEach((metric) => availableMetricNames.add(metric.name));
          }
        });
      } else {
        // If no services selected, show all available metrics
        Object.values(allServiceMetrics).forEach((metrics) => {
          metrics.forEach((metric) => availableMetricNames.add(metric.name));
        });
      }

      setSelectedMetrics((prev) => prev.filter((metric) => availableMetricNames.has(metric)));
    } catch (error) {
      console.error("Error fetching metrics:", error);
      message.error("Failed to fetch metrics");
    } finally {
      setLoadingMetrics(false);
    }
  }, [myHeaders]);

  // Format date to match your required format: 2025-06-27T08:27:21.867000
  const formatDateForAPI = (date) => {
    const isoString = date.toISOString();
    // Remove the 'Z' and add microseconds (000) if not present
    const withoutZ = isoString.replace("Z", "");
    // Ensure we have microseconds format
    if (withoutZ.includes(".")) {
      const [datePart, timePart] = withoutZ.split(".");
      // Pad or truncate to 6 digits for microseconds
      const microseconds = timePart.padEnd(6, "0").substring(0, 6);
      return `${datePart}.${microseconds}`;
    } else {
      return `${withoutZ}.000000`;
    }
  };

  // Handle service selection changes
  const handleServiceSelection = useCallback((services) => {
    // Keep duplicates allowed for services as they might be needed for multiple metrics
    setSelectedServices(services);

    // Detect if a service was removed
    const removed = prevServices.current.filter((s) => !services.includes(s));
    if (removed.length > 0) {
      setSelectedMetrics([]);
    }

    if (services.length === 0) {
      setChartData([]); // clear the chart data if no services selected
      setSelectedMetrics([]);
    }

    // Update previous selection
    prevServices.current = services;
  }, []);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    if (selectedServices.length === 0 || selectedMetrics.length === 0 || dateRange.length !== 2) {
      return;
    }

    setLoadingChart(true);
    try {
      // Build services parameter by repeating each service for each metric it supports
      const servicesForUrl = [];
      selectedMetrics.forEach((metric) => {
        selectedServices.forEach((service) => {
          // Check if this service supports this metric
          if (availableMetrics[service]?.some((m) => m.name === metric)) {
            servicesForUrl.push(service);
          }
        });
      });

      // If no services support the selected metrics, use the original approach
      const servicesParam = servicesForUrl.length > 0 ? servicesForUrl.join(",") : selectedServices.join(",");
      const metricsParam = selectedMetrics.join(",");
      const startDate = formatDateForAPI(dateRange[0].toDate());
      const endDate = formatDateForAPI(dateRange[1].toDate());

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const url = `${api_forfreight_url}/api/plots?services=${servicesParam}&metrics=${metricsParam}&start_date=${startDate}&end_date=${endDate}`;

      const response = await fetch(url, requestOptions);
      const result = await response.json();

      // Transform data for chart
      const transformedData = transformTimeSeriesData(result);
      setChartData(transformedData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      message.error("Failed to fetch chart data");
    } finally {
      setLoadingChart(false);
    }
  }, [selectedServices, selectedMetrics, dateRange, myHeaders, availableMetrics]);

  // Transform time series data for recharts
  const transformTimeSeriesData = (apiData) => {
    const timeMap = new Map();

    apiData.forEach((serviceData) => {
      serviceData.timeseries.forEach((point) => {
        const timestamp = point.timestamp;
        const key = `${serviceData.service}_${serviceData.metric}`;

        if (!timeMap.has(timestamp)) {
          timeMap.set(timestamp, { timestamp });
        }

        timeMap.get(timestamp)[key] = point.value;
      });
    });

    return Array.from(timeMap.values()).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  // Generate chart lines for a specific metric
  const generateChartLinesForMetric = (metric) => {
    const colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8dd1e1", "#d084d0"];
    let colorIndex = 0;

    return selectedServices
      .filter((service) => availableMetrics[service]?.some((m) => m.name === metric))
      .map((service) => (
        <Line
          key={`${service}_${metric}`}
          type="monotone"
          dataKey={`${service}_${metric}`}
          stroke={colors[colorIndex++ % colors.length]}
          strokeWidth={2}
          dot={{ r: 4 }}
          name={service}
          connectNulls={false}
        />
      ));
  };

  // Get chart data filtered for specific metric
  const getChartDataForMetric = (metric) => {
    return chartData
      .map((dataPoint) => {
        const filteredPoint = { timestamp: dataPoint.timestamp };
        selectedServices.forEach((service) => {
          const key = `${service}_${metric}`;
          if (dataPoint[key] !== undefined) {
            filteredPoint[key] = dataPoint[key];
          }
        });
        return filteredPoint;
      })
      .filter((point) => Object.keys(point).length > 1); // Only include points that have data beyond timestamp
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const onRowClick = (record) => {
    navigate("/Results", {
      state: {
        pageToNavigate: "Requesthistory",
        responseData: record.response_body?.additionalProp1,
      },
    });
  };

  const confirmDelete = (id) => {
    setDeleteRequestId(id);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    setLoadingDelete(true);
    const requestOptions = {
      method: "DELETE",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(`${api_forfreight_url}/api/requests/${id}`, requestOptions)
      .then((response) => {
        if (response.ok) {
          fetchData();          
          message.success("Request deleted successfully");    

        } else {
          message.error("Failed to delete request");
        }
      })
      .catch((error) => {
        console.error(error);
        message.error("Error occurred while deleting");
      })
      .finally(() => {
        setLoadingDelete(false);
        setIsModalVisible(false);
        setDeleteRequestId(null);
      });
  };

  // Get unique services from data
  const uniqueServices = useMemo(
    () => Array.from(new Set(data.map((item) => item.request_service))).filter(Boolean),
    [data]
  );

  // Get all available metric names from selected services
  const allAvailableMetrics = useMemo(() => {
    const metrics = new Set();
    Object.values(availableMetrics).forEach((serviceMetrics) => {
      serviceMetrics.forEach((metric) => metrics.add(metric.name));
    });
    return Array.from(metrics);
  }, [availableMetrics]);

  // Get metrics that are available for at least one selected service
  const getAvailableMetricsForServices = useMemo(() => {
    if (selectedServices.length === 0) return [];

    const metricsMap = new Map();
    selectedServices.forEach((service) => {
      if (availableMetrics[service]) {
        availableMetrics[service].forEach((metric) => {
          if (!metricsMap.has(metric.name)) {
            metricsMap.set(metric.name, {
              name: metric.name,
              format: metric.format,
              services: [service],
            });
          } else {
            metricsMap.get(metric.name).services.push(service);
          }
        });
      }
    });

    return Array.from(metricsMap.values());
  }, [selectedServices, availableMetrics]);

  const columns = useMemo(
    () => [
      {
        title: "DateTime",
        dataIndex: "request_datetime",
        key: "request_datetime",
        align: "center",
        sorter: (a, b) => new Date(a.request_datetime) - new Date(b.request_datetime),
        render: (text) => new Date(text).toLocaleString(),
        filters: Array.from(new Set(data.map((item) => new Date(item.request_datetime).toLocaleDateString()))).map(
          (date) => ({
            text: date,
            value: date,
          })
        ),
        onFilter: (value, record) => new Date(record.request_datetime).toLocaleDateString() === value,
      },
      {
        title: "Service",
        dataIndex: "request_service",
        key: "request_service",
        responsive: ["md"],
        align: "center",
        filters: Array.from(new Set(data.map((item) => item.request_service))).map((service) => ({
          text: service,
          value: service,
        })),
        onFilter: (value, record) => record.request_service === value,
      },
      {
        title: "Endpoint",
        dataIndex: "request_endpoint",
        key: "request_endpoint",
        responsive: ["lg"],
        align: "center",
      },
      {
        title: "Method",
        dataIndex: "request_method",
        key: "request_method",
        responsive: ["lg"],
        align: "center",
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        render: (_, record) => (
          <>
            <button
              className="row-hover-button"
              onClick={(e) => {
                e.stopPropagation();
                onRowClick(record);
              }}
            >
              View Details
            </button>
            <button
              className="delete-button"
              style={{
                marginLeft: 8,
                color: "red",
                border: "none",
                background: "none",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                confirmDelete(record.id);
              }}
            >
              Delete
            </button>
          </>
        ),
      },
    ],
    [data]
  );

  // Updated TableContent component with loading state
  const TableContent = () => (
    <div className="request-history-table">
      {loadingData ? (
        <Card>
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, fontSize: "16px", color: "#666" }}>
              Loading request data...
            </p>
          </div>
        </Card>
      ) : (
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loadingData} // Alternative: you can also use this built-in loading prop
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            className: "custom-row",
            style: { cursor: "pointer" },
          })}
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: "max-content" }}
          bordered
          size="middle"
          locale={{ emptyText: "No data available" }}
          style={{
            background: "#F5F5F5",
            borderRadius: 8,
          }}
        />
      )}
    </div>
  );

  const ChartContent = () => (
    <div style={{ padding: 20 }}>
      <Card title="Time Series Chart Configuration" style={{ marginBottom: 20, height: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Service Selection */}
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>Select Services:</label>
            <Select
              mode="multiple"
              placeholder="Choose services"
              value={selectedServices}
              onChange={handleServiceSelection}
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {availableServices.map((serviceObj, index) => (
                <Option key={index} value={serviceObj.service}>
                  {serviceObj.description} - {serviceObj.service}
                </Option>
              ))}
            </Select>
            {selectedServices.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                Selected: {selectedServices.join(", ")}
              </div>
            )}
          </div>

          {/* Metrics Selection */}
          {selectedServices.length > 0 && (
            <div>
              <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>Select Metrics:</label>
              {loadingMetrics ? (
                <Spin size="small" />
              ) : getAvailableMetricsForServices.length > 0 ? (
                <div>
                  <Checkbox.Group value={selectedMetrics} onChange={setSelectedMetrics} style={{ width: "100%" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {getAvailableMetricsForServices.map((metric) => (
                        <div key={metric.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Checkbox value={metric.name}>
                            <span style={{ fontWeight: "bold" }}>{metric.name.replace(/_/g, " ")}</span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#666",
                                marginLeft: 8,
                                fontStyle: "italic",
                              }}
                            >
                              ({metric.format}) - Available in: {metric.services.join(", ")}
                            </span>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Checkbox.Group>
                  {selectedMetrics.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                      Selected metrics: {selectedMetrics.join(", ")}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: 16,
                    background: "#fff2f0",
                    border: "1px solid #ffccc7",
                    borderRadius: 6,
                    color: "#a8071a",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: "bold" }}>No metrics available</p>
                  <p style={{ margin: "4px 0 0 0", fontSize: "14px" }}>
                    No metrics found for the selected services. Please select other services that have available
                    metrics.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Date Range Selection */}
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>Select Date Range:</label>
            <RangePicker
              showTime
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
              placeholder={["Start Date", "End Date"]}
            />
          </div>

          {/* Generate Chart Button */}
          <Button
            type="primary"
            onClick={fetchChartData}
            disabled={
              selectedServices.length === 0 ||
              selectedMetrics.length === 0 ||
              dateRange.length !== 2 ||
              getAvailableMetricsForServices.length === 0
            }
            loading={loadingChart}
            style={{ alignSelf: "flex-start" }}
          >
            Generate Charts
          </Button>
        </div>
      </Card>

      {/* Charts Display - Individual charts for each selected metric */}
      {loadingChart ? (
        <Card>
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading chart data...</p>
          </div>
        </Card>
      ) : chartData.length > 0 && selectedMetrics.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Individual Charts for Each Selected Metric */}
          {selectedMetrics.map((metric, index) => {
            const metricData = getChartDataForMetric(metric);
            const servicesWithThisMetric = selectedServices.filter((service) =>
              availableMetrics[service]?.some((m) => m.name === metric)
            );

            return metricData.length > 0 ? (
              <Card
                key={metric}
                title={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        padding: "4px 12px",
                        backgroundColor: "#1890ff",
                        color: "white",
                        borderRadius: 16,
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      CHART {index + 1}
                    </span>
                    <span style={{ fontSize: "18px", fontWeight: "bold", textTransform: "capitalize" }}>
                      {metric.replace(/_/g, " ")}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      ({servicesWithThisMetric.length} service{servicesWithThisMetric.length !== 1 ? "s" : ""}:{" "}
                      {servicesWithThisMetric.join(", ")})
                    </span>
                  </div>
                }
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
              >
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={metricData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                    <YAxis
                      label={{
                        value: metric.replace(/_/g, " "),
                        angle: -90,
                        position: "insideLeft",
                        style: { textAnchor: "middle" },
                      }}
                    />
                    <Tooltip
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                      formatter={(value, name) => [
                        typeof value === "number" ? value.toFixed(4) : value,
                        `${name} (${metric.replace(/_/g, " ")})`,
                      ]}
                    />
                    <Legend />
                    {generateChartLinesForMetric(metric)}
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            ) : (
              <Card
                key={metric}
                title={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        padding: "4px 12px",
                        backgroundColor: "#ff4d4f",
                        color: "white",
                        borderRadius: 16,
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      NO DATA
                    </span>
                    <span style={{ fontSize: "18px", fontWeight: "bold", textTransform: "capitalize" }}>
                      {metric.replace(/_/g, " ")}
                    </span>
                  </div>
                }
                style={{ boxShadow: "0 4px 12px rgba(255, 77, 79, 0.1)" }}
              >
                <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
                  <p>No data available for this metric in the selected date range.</p>
                  <p>Try adjusting your service selection or date range.</p>
                </div>
              </Card>
            );
          })}
        </div>
      ) : selectedServices.length > 0 && selectedMetrics.length > 0 && dateRange.length === 2 ? (
        <Card>
          <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
            <p>No data available for the selected parameters.</p>
            <p>Try adjusting your service, metrics, or date range selection.</p>
          </div>
        </Card>
      ) : null}
    </div>
  );

  return (
    <AppLayout>
      <h1 style={{ marginBottom: 20, color: "#142745" }}>Request History</h1>

      <div style={{ padding: 20 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          style={{
            background: "#fff",
            borderRadius: 8,
            padding: "16px 20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <TabPane
            tab={
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: activeTab === "1" ? "600" : "400",
                  color: activeTab === "1" ? "#142745" : "#666",
                }}
              >
                📋 Data Table
              </span>
            }
            key="1"
          >
            <div style={{ marginTop: 20 }}>
              <TableContent />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: activeTab === "2" ? "600" : "400",
                  color: activeTab === "2" ? "#142745" : "#666",
                }}
              >
                📈 Analytics Data
              </span>
            }
            key="2"
          >
            <ChartContent />
          </TabPane>
        </Tabs>
      </div>

      <Modal title="Confirm Deletion" open={isModalVisible} footer={null} onCancel={() => setIsModalVisible(false)}>
        <p>Are you sure you want to delete this request?</p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 24,
          }}
        >
          <Button
            className="button-yes"
            loading={loadingDelete}
            style={{
              padding: "6px 12px",
              background: "#f5222d",
              color: "white",
              border: "none",
              borderRadius: 4,
            }}
            onClick={() => handleDelete(deleteRequestId)}
          >
            Yes
          </Button>
          <Button
            style={{
              padding: "6px 12px",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
            }}
            onClick={() => setIsModalVisible(false)}
          >
            No
          </Button>
        </div>
      </Modal>
    </AppLayout>
  );
};

export default Requesthistory;